import { Injectable } from '@angular/core';
import { ErrorSanitizerService } from '@core/services/error-sanitizer.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, forkJoin, from, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  pairwise,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  CompositeActions,
  LocationExcludedExposureActions,
  LocationExposureActions,
  RetrieveActions,
  UnderlyingPolicyActions,
  VehicleExcludedExposureActions,
  VehicleExposureActions,
  WatercraftExcludedExposureActions,
  WatercraftExposureActions,
} from '@core/store/actions';
import {
  ProductsSelectors,
  UnderlyingPolicySelector,
} from '@core/store/selectors';
import { UnderlyingPolicyService } from '@core/services/underlying-policy.service';
import {
  ActionAggregatorService,
  ExpectedActions,
} from '@core/services/action-aggregator.service';
import { UmbrellaPageUtils } from '@shared/utils/pages/umbrella-page.utils';
import { getAllExposures } from '@entities/umbrella/umbrella.selector';
import { UnderlyingPolicyEntity } from './underlying-policy.entity';
import { ProductModel } from '@entities/product/product.model';
import { LocationExposureEntity } from '@entities/exposures/location-exposures/location-exposures.entity';
import { VehicleExposureEntity } from '@entities/exposures/vehicle-exposures/vehicle-exposures.entity';
import { WatercraftExposureEntity } from '@entities/exposures/watercraft-exposures/watercraft-exposures.entity';
import { LocationExcludedExposureEntity } from '@entities/exposures/location-excluded-exposures/location-excluded-exposures.entity';
import { VehicleExcludedExposureEntity } from '@entities/exposures/vehicle-excluded-exposures/vehicle-excluded-exposures.entity';
import { WatercraftExcludedExposureEntity } from '@entities/exposures/watercraft-excluded-exposures/watercraft-excluded-exposures.entity';
import { VehicleExposureService } from '@core/services/vehicle-exposure.service';
import { WatercraftExposureService } from '@core/services/watercraft-exposure.service';
import { LocationExposureService } from '@core/services/location-exposure.service';
import { Nullable } from '@shared/utils/type.utils';
import { filterOutNull } from '@shared/rxjs/filter-out-null.operator';
import { ProductsService } from '@core/services/products.service';

@Injectable()
export class UnderlyingPolicyEffect {
  constructor(
    private actions$: Actions,
    private store: Store,
    private service: UnderlyingPolicyService,
    private errorSanitizerService: ErrorSanitizerService,
    private aggregatorService: ActionAggregatorService,
    private vehicleExposureService: VehicleExposureService,
    private locationExposureService: LocationExposureService,
    private watercraftExposureService: WatercraftExposureService,
    private productsService: ProductsService
  ) {}

  upsertUnderlyingPolicy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UnderlyingPolicyActions.upsertUnderlyingPolicy),
        mergeMap((action) =>
          this.store
            .select(
              UnderlyingPolicySelector.buildUnderlyingPolicyRequest(
                action.payload,
                action.correlationId || ''
              )
            )
            .pipe(
              take(1),
              map((request) => ({ request, action }))
            )
        ),
        mergeMap(({ request, action }) => {
          return this.service.addOrUpdateUnderlyingPolicy(request).pipe(
            mergeMap((response) => {
              return from([
                UnderlyingPolicyActions.upsertUnderlyingPolicySuccess({
                  payload: response,
                  existingPolicyNumber: action.existingPolicyNumber || '',
                  correlationId: request.correlationId,
                }),
              ]);
            }),
            catchError((error) => {
              const saneError = this.errorSanitizerService.sanitizeError(
                error,
                request.productType
              );
              return of(
                UnderlyingPolicyActions.upsertUnderlyingPolicyFail({
                  error: saneError,
                  entityId:
                    request.underlyingPolicyBody.underlyingPolicy
                      .underlyingPolicyId,
                  correlationId: request.correlationId,
                })
              );
            })
          );
        })
      ) as Observable<Action>
  );

  restoreUnderlyingPolicies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UnderlyingPolicyActions.restoreUnderlyingPolicies),
      exhaustMap((actions) =>
        this.store
          .select(UnderlyingPolicySelector.getAllDeletedUnderlyingPolicies)
          .pipe(take(1))
      ),
      mergeMap((underlyingPolicies) => {
        const nextActions: Action[] = [];
        const expectedActions: ExpectedActions = [];

        underlyingPolicies.forEach((policy) => {
          UmbrellaPageUtils.updateUnderlyingPolicy(
            {
              ...policy,
              underlyingPolicyId: '',
            },
            nextActions,
            expectedActions
          );
        });

        nextActions.push(
          UnderlyingPolicyActions.clearDeletedUnderlyingPolicies()
        );

        this.aggregatorService.aggregate(expectedActions, {
          onSuccess: (store, actions) => {
            store.dispatch(
              CompositeActions.upsertUnderlyingPoliciesSuccess({
                payload: actions,
              })
            );
          },
          onError: (store, actions) => {
            store.dispatch(
              CompositeActions.upsertUnderlyingPoliciesError({
                payload: actions,
              })
            );
          },
          onTimeout: (store) => {
            store.dispatch(
              CompositeActions.upsertUnderlyingPoliciesError({
                payload: {
                  reason: 'Timeout',
                  message: 'Timeout submitting Umbrella',
                },
              })
            );
          },
        });

        nextActions.forEach((action) => {
          this.store.dispatch(action);
        });

        return this.actions$.pipe(
          ofType(
            CompositeActions.upsertUnderlyingPoliciesSuccess,
            CompositeActions.upsertUnderlyingPoliciesError
          ),
          take(1),
          switchMap((action) => {
            const exposures: Action[] = action.payload.map((policy: any) => {
              return CompositeActions.restoreExposure({
                payload: policy.payload,
              });
            });
            return from(exposures);
          }),
          catchError((error) => {
            const saneError = this.errorSanitizerService.sanitizeError(error);
            return of(
              CompositeActions.upsertUnderlyingPoliciesError({
                payload: saneError,
              })
            );
          })
        );
      }),
      catchError((error) => {
        const saneError = this.errorSanitizerService.sanitizeError(error);
        return of(
          UnderlyingPolicyActions.upsertUnderlyingPolicyFail({
            error: saneError,
          })
        );
      })
    )
  );

  deleteUnderlyingPolicy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UnderlyingPolicyActions.deleteUnderlyingPolicy),
        mergeMap((action) =>
          this.store
            .select(
              UnderlyingPolicySelector.buildUnderlyingPolicyRequest(
                action.payload,
                action.correlationId || ''
              )
            )
            .pipe(take(1))
        ),
        mergeMap((request) => {
          return this.service.deleteUnderlyingPolicy(request).pipe(
            mergeMap(() => {
              return from([
                UnderlyingPolicyActions.deleteUnderlyingPolicySuccess({
                  underlyingPolicyId:
                    request.underlyingPolicyBody.underlyingPolicy
                      .underlyingPolicyId,
                  underlyingPolicyNumber:
                    request.underlyingPolicyBody.underlyingPolicy
                      .underlyingPolicyNumber,
                }),
              ]);
            }),
            catchError((error) => {
              const saneError = this.errorSanitizerService.sanitizeError(
                error,
                request.productType
              );
              return of(
                UnderlyingPolicyActions.deleteUnderlyingPolicyFail({
                  error: saneError,
                  entityId:
                    request.underlyingPolicyBody.underlyingPolicy
                      .underlyingPolicyId,
                })
              );
            })
          );
        })
      ) as Observable<Action>
  );

  /* Operates on underlyingPolicies and all the various Exposure things.
   * It's the same basic idea as VersionedQuoteEffect.generateUpdateCallsForUmbrellaBits,
   * but can be triggered in normal non-version-related circumstances.
   */
  forceUmbrellaBitsToAgreeWithExistingQuotes$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RetrieveActions.allRetrievesComplete),
        withLatestFrom(
          this.store.select(UnderlyingPolicySelector.getAllUnderlyingPolicies),
          this.store.select(getAllExposures),
          this.store.select(ProductsSelectors.getSelectedProductEntities)
        ),
        filter(([action, underlyingPolicies, exposures, products]) => {
          return !!products.find((p) => p.type === 'PersonalUmbrella');
        }),
        switchMap(([action, underlyingPolicies, exposures, products]) => {
          const calls: Observable<unknown>[] = [];
          for (const underlyingPolicy of underlyingPolicies) {
            this.updateUnderlyingPolicyForVersions(
              calls,
              underlyingPolicy,
              products
            );
          }
          for (const exposure of exposures.locationExposures) {
            this.updateLocationExposureForVersions(calls, exposure, products);
          }
          for (const exposure of exposures.vehicleExposures) {
            this.updateVehicleExposureForVersions(calls, exposure, products);
          }
          for (const exposure of exposures.watercraftExposures) {
            this.updateWatercraftExposureForVersions(calls, exposure, products);
          }
          for (const exposure of exposures.locationExcludedExposures) {
            this.updateLocationExcludedExposureForVersions(
              calls,
              exposure,
              products
            );
          }
          for (const exposure of exposures.vehicleExcludedExposures) {
            this.updateVehicleExcludedExposureForVersions(
              calls,
              exposure,
              products
            );
          }
          for (const exposure of exposures.watercraftExcludedExposures) {
            this.updateWatercraftExcludedExposureForVersions(
              calls,
              exposure,
              products
            );
          }
          return forkJoin(calls);
        })
      ),
    { dispatch: false }
  );

  private updateUnderlyingPolicyForVersions(
    calls: Observable<unknown>[],
    underlyingPolicy: UnderlyingPolicyEntity,
    products: ProductModel[]
  ): void {
    const realQuoteId = this.unfuzzQuoteId(
      products,
      underlyingPolicy.underlyingPolicyNumber
    );
    if (
      realQuoteId &&
      realQuoteId !== underlyingPolicy.underlyingPolicyNumber
    ) {
      calls.push(
        this.service
          .addOrUpdateUnderlyingPolicy({
            quoteId: products.find((p) => p.type === 'PersonalUmbrella')!
              .quoteId,
            underlyingPolicyBody: {
              underlyingPolicy: {
                ...underlyingPolicy,
                underlyingPolicyNumber: realQuoteId,
              },
            },
          })
          .pipe(
            tap((rsp) =>
              this.store.dispatch(
                UnderlyingPolicyActions.upsertUnderlyingPolicySuccess({
                  payload: rsp,
                  existingPolicyNumber: '',
                })
              )
            ),
            catchError((e) => from([]))
          )
      );
    }
  }

  private updateLocationExposureForVersions(
    calls: Observable<unknown>[],
    exposure: LocationExposureEntity,
    products: ProductModel[]
  ): void {
    const realQuoteId = this.unfuzzQuoteId(
      products,
      exposure.underlyingPolicyNumber
    );
    if (realQuoteId && realQuoteId !== exposure.underlyingPolicyNumber) {
      calls.push(
        this.locationExposureService
          .addOrUpdateLocationExposure({
            quoteId: products.find((p) => p.type === 'PersonalUmbrella')!
              .quoteId,
            locationExposureBody: {
              locationExposure: {
                ...exposure,
                underlyingPolicyNumber: realQuoteId,
              },
            },
          })
          .pipe(
            tap((rsp) =>
              this.store.dispatch(
                LocationExposureActions.upsertLocationExposureSuccess({
                  payload: rsp,
                  oldLocationExposureId: '',
                })
              )
            ),
            catchError((e) => from([]))
          )
      );
    }
  }

  private updateVehicleExposureForVersions(
    calls: Observable<unknown>[],
    exposure: VehicleExposureEntity,
    products: ProductModel[]
  ): void {
    const realQuoteId = this.unfuzzQuoteId(
      products,
      exposure.underlyingPolicyNumber
    );
    if (realQuoteId && realQuoteId !== exposure.underlyingPolicyNumber) {
      calls.push(
        this.vehicleExposureService
          .addOrUpdateVehicleExposure({
            quoteId: products.find((p) => p.type === 'PersonalUmbrella')!
              .quoteId,
            vehicleExposureBody: {
              vehicleExposure: {
                ...exposure,
                underlyingPolicyNumber: realQuoteId,
              },
            },
          })
          .pipe(
            tap((rsp) =>
              this.store.dispatch(
                VehicleExposureActions.upsertVehicleExposureSuccess({
                  payload: rsp,
                })
              )
            ),
            catchError((e) => from([]))
          )
      );
    }
  }

  private updateWatercraftExposureForVersions(
    calls: Observable<unknown>[],
    exposure: WatercraftExposureEntity,
    products: ProductModel[]
  ): void {
    const realQuoteId = this.unfuzzQuoteId(
      products,
      exposure.underlyingPolicyNumber
    );
    if (realQuoteId && realQuoteId !== exposure.underlyingPolicyNumber) {
      calls.push(
        this.watercraftExposureService
          .addOrUpdateWatercraftExposure({
            quoteId: products.find((p) => p.type === 'PersonalUmbrella')!
              .quoteId,
            watercraftExposureBody: {
              watercraftExposure: {
                ...exposure,
                underlyingPolicyNumber: realQuoteId,
              },
            },
          })
          .pipe(
            tap((rsp) =>
              this.store.dispatch(
                WatercraftExposureActions.upsertWatercraftExposureSuccess({
                  payload: rsp,
                })
              )
            ),
            catchError((e) => from([]))
          )
      );
    }
  }

  private updateLocationExcludedExposureForVersions(
    calls: Observable<unknown>[],
    exposure: LocationExcludedExposureEntity,
    products: ProductModel[]
  ): void {
    const realQuoteId = this.unfuzzQuoteId(
      products,
      exposure.underlyingPolicyNumber
    );
    if (realQuoteId && realQuoteId !== exposure.underlyingPolicyNumber) {
      calls.push(
        this.locationExposureService
          .addOrUpdateLocationExcludedExposure({
            quoteId: products.find((p) => p.type === 'PersonalUmbrella')!
              .quoteId,
            locationExcludedExposureBody: {
              locationExcludedExposure: {
                ...exposure,
                underlyingPolicyNumber: realQuoteId,
              },
            },
          })
          .pipe(
            tap((rsp) =>
              this.store.dispatch(
                LocationExcludedExposureActions.upsertLocationExcludedExposureSuccess(
                  {
                    payload: rsp,
                  }
                )
              )
            ),
            catchError((e) => from([]))
          )
      );
    }
  }

  private updateVehicleExcludedExposureForVersions(
    calls: Observable<unknown>[],
    exposure: VehicleExcludedExposureEntity,
    products: ProductModel[]
  ): void {
    const realQuoteId = this.unfuzzQuoteId(
      products,
      exposure.underlyingPolicyNumber
    );
    if (realQuoteId && realQuoteId !== exposure.underlyingPolicyNumber) {
      calls.push(
        this.vehicleExposureService
          .addOrUpdateExcludedVehicleExposure({
            quoteId: products.find((p) => p.type === 'PersonalUmbrella')!
              .quoteId,
            vehicleExcludedExposureBody: {
              vehicleExcludedExposure: {
                ...exposure,
                underlyingPolicyNumber: realQuoteId,
              },
            },
          })
          .pipe(
            tap((rsp) =>
              this.store.dispatch(
                VehicleExcludedExposureActions.upsertVehicleExcludedExposureSuccess(
                  {
                    payload: rsp,
                  }
                )
              )
            ),
            catchError((e) => from([]))
          )
      );
    }
  }

  private updateWatercraftExcludedExposureForVersions(
    calls: Observable<unknown>[],
    exposure: WatercraftExcludedExposureEntity,
    products: ProductModel[]
  ): void {
    const realQuoteId = this.unfuzzQuoteId(
      products,
      exposure.underlyingPolicyNumber
    );
    if (realQuoteId && realQuoteId !== exposure.underlyingPolicyNumber) {
      calls.push(
        this.watercraftExposureService
          .addOrUpdateWatercraftExcludedExposure({
            quoteId: products.find((p) => p.type === 'PersonalUmbrella')!
              .quoteId,
            watercraftExcludedExposureBody: {
              watercraftExcludedExposure: {
                ...exposure,
                underlyingPolicyNumber: realQuoteId,
              },
            },
          })
          .pipe(
            tap((rsp) =>
              this.store.dispatch(
                WatercraftExcludedExposureActions.upsertWatercraftExcludedExposureSuccess(
                  {
                    payload: rsp,
                  }
                )
              )
            ),
            catchError((e) => from([]))
          )
      );
    }
  }

  private unfuzzQuoteId(
    products: ProductModel[],
    fuzzy: Nullable<string>
  ): string {
    if (fuzzy) {
      const stem = fuzzy.split('-')[0];
      for (const product of products) {
        if (product.quoteId?.split('-')[0] === stem) {
          return product.quoteId;
        }
      }
    }
    return '';
  }

  /* TIB-2446: When any quote's effectiveDate changes, check whether there's a related underlyingPolicy.
   * If so, update the underlyingPolicy too. DSM does not do this automatically.
   */
  updateUnderlyingPoliciesWhenEffectiveDateChanges$ = createEffect(
    () =>
      this.store.select(ProductsSelectors.getSelectedProductEntities).pipe(
        pairwise(),
        map(([prev, next]) => {
          const changed = this.productsWithChangedEffectiveDate(prev, next);
          const umbrella = next.find((p) => p.type === 'PersonalUmbrella');
          return { changed, umbrella };
        }),
        filter(({ changed, umbrella }) => !!changed.length && !!umbrella),
        withLatestFrom(this.service.getAllUnderlyingPolicies()),
        map(([{ changed, umbrella }, underlyingPolicies]) => {
          for (const product of changed) {
            const underlyingPolicy = underlyingPolicies.find((up) => {
              return up.underlyingPolicyNumber === product.quoteId;
            });
            if (
              underlyingPolicy &&
              underlyingPolicy.effectiveDate !== product.effectiveDate
            ) {
              this.forceUmbrellaEffectiveDateIfEarlier(
                umbrella!,
                product.effectiveDate!
              )
                .then(() => {
                  this.service.upsertUnderlyingPolicy({
                    ...underlyingPolicy,
                    effectiveDate: product.effectiveDate!,
                  });
                })
                .catch((e) => {
                  // The updateQuote will fail thru the usual channels. Don't proceed with the underlying policy.
                });
            }
          }
          return null;
        })
      ),
    { dispatch: false }
  );

  private forceUmbrellaEffectiveDateIfEarlier(
    umbrella: ProductModel,
    effectiveDate: string
  ): Promise<any> {
    if (effectiveDate.localeCompare(umbrella.effectiveDate || '') <= 0) {
      return Promise.resolve();
    }
    return this.productsService.updateQuoteAndAwaitResponse({
      productType: 'PersonalUmbrella',
      effectiveDate,
    });
  }

  /* Nothing-to-something and something-to-nothing don't count as changes.
   * We return models from (b), presumably those are the newer ones.
   */
  private productsWithChangedEffectiveDate(
    a: ProductModel[],
    b: ProductModel[]
  ): ProductModel[] {
    if (!a || !b) {
      return [];
    }
    return b.filter((bproduct) => {
      const aproduct = a.find((p) => p.type === bproduct.type);
      if (!aproduct) return false;
      if (aproduct.effectiveDate === bproduct.effectiveDate) return false;
      return true;
    });
  }
}
